import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  LayoutDashboard,
  Users,
  BookOpen,
  ClipboardList,
  Tag,
  BarChart2,
  Map,
  Settings,
  LogOut,
  ChevronRight,
  Sun,
  Moon,
} from "lucide-react";

type Theme = "light" | "dark";

const THEME_KEY = "dc-theme";

function getStoredTheme(): Theme {
  return localStorage.getItem(THEME_KEY) === "dark" ? "dark" : "light";
}

interface NavItem {
  to: string;
  label: string;
  icon: React.ElementType;
  adminOnly?: boolean;
  soon?: boolean;
}

const navItems: NavItem[] = [
  { to: "/dashboard",           label: "Overview",   icon: LayoutDashboard },
  { to: "/dashboard/voters",    label: "Voters",     icon: Users },
  { to: "/dashboard/walkbooks", label: "Walkbooks",  icon: BookOpen,      soon: true },
  { to: "/dashboard/surveys",   label: "Surveys",    icon: ClipboardList },
  { to: "/dashboard/tags",      label: "Tags",       icon: Tag,           adminOnly: true },
  { to: "/dashboard/reports",   label: "Reports",    icon: BarChart2,     soon: true },
  { to: "/dashboard/map",       label: "Map",        icon: Map,           soon: true },
  { to: "/dashboard/users",     label: "Users",      icon: Users,         adminOnly: true },
  { to: "/dashboard/settings",  label: "Settings",   icon: Settings,      soon: true },
];

export function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [theme, setTheme] = useState<Theme>(getStoredTheme);

  useEffect(() => {
    localStorage.setItem(THEME_KEY, theme);
  }, [theme]);

  async function handleLogout() {
    await logout();
    void navigate("/login");
  }

  const visibleNav = navItems.filter(
    (item) => !item.adminOnly || user?.role === "admin"
  );

  // Split into main nav and bottom nav (Settings)
  const mainNav = visibleNav.filter((item) => item.to !== "/dashboard/settings");
  const bottomNav = visibleNav.filter((item) => item.to === "/dashboard/settings");

  return (
    // The dark class is scoped to the dashboard — the infosite stays light.
    <div className={`${theme === "dark" ? "dark " : ""}min-h-screen flex bg-muted/40 text-foreground`} style={{ colorScheme: theme }}>
      {/* Sidebar */}
      <aside className="w-60 shrink-0 bg-background border-r flex flex-col">

        {/* Logo */}
        <div className="h-16 flex items-center gap-3 px-5 border-b">
          <div className="w-8 h-8 rounded-md bg-primary flex items-center justify-center shrink-0">
            <span className="text-white text-xs font-bold tracking-tight">DC</span>
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold tracking-tight leading-none">Direct Campaign</p>
            <p className="text-xs text-muted-foreground mt-0.5 truncate">
              {user?.name ?? user?.email ?? "Dashboard"}
            </p>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          {mainNav.map((item) => {
            const Icon = item.icon;
            if (item.soon) {
              return (
                <div
                  key={item.to}
                  className="flex items-center gap-2.5 rounded-md px-3 py-2 text-sm text-muted-foreground/50 cursor-default select-none"
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  <span className="flex-1">{item.label}</span>
                  <span className="text-[10px] font-medium bg-muted text-muted-foreground/60 rounded px-1.5 py-0.5 leading-none">
                    Soon
                  </span>
                </div>
              );
            }
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/dashboard"}
                className={({ isActive }) =>
                  [
                    "flex items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors",
                    isActive
                      ? "bg-primary/8 text-primary font-medium"
                      : "text-muted-foreground hover:text-foreground hover:bg-accent/60",
                  ].join(" ")
                }
              >
                {({ isActive }) => (
                  <>
                    <Icon className="w-4 h-4 shrink-0" />
                    <span className="flex-1">{item.label}</span>
                    {isActive && <ChevronRight className="w-3 h-3 opacity-40" />}
                  </>
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* Bottom: Settings + User */}
        <div className="border-t px-3 py-3 space-y-0.5">
          {bottomNav.map((item) => {
            const Icon = item.icon;
            if (item.soon) {
              return (
                <div
                  key={item.to}
                  className="flex items-center gap-2.5 rounded-md px-3 py-2 text-sm text-muted-foreground/50 cursor-default select-none"
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  <span className="flex-1">{item.label}</span>
                  <span className="text-[10px] font-medium bg-muted text-muted-foreground/60 rounded px-1.5 py-0.5 leading-none">
                    Soon
                  </span>
                </div>
              );
            }
            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  [
                    "flex items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors",
                    isActive
                      ? "bg-primary/8 text-primary font-medium"
                      : "text-muted-foreground hover:text-foreground hover:bg-accent/60",
                  ].join(" ")
                }
              >
                {({ isActive }) => (
                  <>
                    <Icon className="w-4 h-4 shrink-0" />
                    <span className="flex-1">{item.label}</span>
                    {isActive && <ChevronRight className="w-3 h-3 opacity-40" />}
                  </>
                )}
              </NavLink>
            );
          })}

          {/* User row */}
          <div className="pt-2 mt-1 border-t">
            <div className="flex items-center gap-2.5 px-3 py-2">
              <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <span className="text-[10px] font-semibold text-primary uppercase">
                  {(user?.name ?? user?.email ?? "?")[0]}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium truncate leading-none">{user?.name ?? "You"}</p>
                <p className="text-[10px] text-muted-foreground capitalize mt-0.5">{user?.role}</p>
              </div>
              <button
                onClick={() => setTheme((t) => (t === "dark" ? "light" : "dark"))}
                className="text-muted-foreground/60 hover:text-foreground transition-colors"
                title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              >
                {theme === "dark" ? (
                  <Sun className="w-3.5 h-3.5" />
                ) : (
                  <Moon className="w-3.5 h-3.5" />
                )}
              </button>
              <button
                onClick={() => void handleLogout()}
                className="text-muted-foreground/60 hover:text-foreground transition-colors"
                title="Sign out"
              >
                <LogOut className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        <main className="flex-1 p-8 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
